import React, { useEffect, useState, useRef } from 'react';
import { BellIcon, SendIcon, ImageIcon, CalendarIcon, RefreshCwIcon, AlertCircleIcon, CheckIcon, MailIcon } from 'lucide-react';
import { ActivityLogEntry } from '../../data/types';
interface NotificationBellProps {
  entries: ActivityLogEntry[];
  unreadCount: number;
  onMarkAllRead: () => void;
  onMarkRead: (id: string) => void;
}
export function NotificationBell({
  entries,
  unreadCount,
  onMarkAllRead,
  onMarkRead
}: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'activity' | 'emails'>('activity');
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const activityEntries = entries.filter(e => e.type !== 'email');
  const emailEntries = entries.filter(e => e.type === 'email');
  const getIcon = (type: ActivityLogEntry['type']) => {
    switch (type) {
      case 'request':
        return <SendIcon className="w-4 h-4 text-blue-500" data-id="element-927" />;
      case 'artwork':
        return <ImageIcon className="w-4 h-4 text-purple-500" data-id="element-928" />;
      case 'booking':
        return <CalendarIcon className="w-4 h-4 text-green-500" data-id="element-929" />;
      case 'slot-change':
        return <RefreshCwIcon className="w-4 h-4 text-orange-500" data-id="element-930" />;
      case 'system':
        return <AlertCircleIcon className="w-4 h-4 text-gray-500" data-id="element-931" />;
      case 'email':
        return <MailIcon className="w-4 h-4 text-teal-500" data-id="element-932" />;
    }
  };
  // Simple relative time formatter
  const getRelativeTime = (timestamp: string) => {
    const rtf = new Intl.RelativeTimeFormat('en', {
      numeric: 'auto'
    });
    const daysDifference = Math.round((new Date(timestamp).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    const hoursDifference = Math.round((new Date(timestamp).getTime() - new Date().getTime()) / (1000 * 60 * 60));
    const minutesDifference = Math.round((new Date(timestamp).getTime() - new Date().getTime()) / (1000 * 60));
    if (Math.abs(minutesDifference) < 60) {
      return rtf.format(minutesDifference, 'minute');
    } else if (Math.abs(hoursDifference) < 24) {
      return rtf.format(hoursDifference, 'hour');
    } else {
      return rtf.format(daysDifference, 'day');
    }
  };
  const displayedEntries = activeTab === 'activity' ? activityEntries : emailEntries;
  return <div ref={wrapperRef} className="relative" data-id="element-933">
      <button onClick={() => setIsOpen(!isOpen)} className="relative p-2 text-text-secondary hover:text-primary transition-colors rounded-full hover:bg-surface focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2" aria-label="Notifications" data-id="element-934">
        <BellIcon className="w-5 h-5" data-id="element-935" />
        {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 block h-2 w-2 rounded-full bg-urgent ring-2 ring-white" data-id="element-936" />}
      </button>

      {isOpen && <div className="absolute right-0 mt-2 w-96 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50 overflow-hidden" data-id="element-937">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-surface-warm" data-id="element-938">
            <h3 className="text-sm font-semibold text-text-primary" data-id="element-939">
              Notifications
            </h3>
            {activeTab === 'activity' && unreadCount > 0 && <button onClick={() => {
          onMarkAllRead();
          setIsOpen(false);
        }} className="text-xs text-primary hover:text-primary-dark font-medium flex items-center gap-1" data-id="element-940">
                <CheckIcon className="w-3 h-3" data-id="element-941" />
                Mark all read
              </button>}
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border bg-white" data-id="element-942">
            <button onClick={() => setActiveTab('activity')} className={`flex-1 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors relative ${activeTab === 'activity' ? 'text-primary' : 'text-text-tertiary hover:text-text-secondary'}`} data-id="element-943">
              Activity
              {unreadCount > 0 && <span className="ml-1.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-urgent text-white text-[10px] font-bold" data-id="element-944">
                  {unreadCount}
                </span>}
              {activeTab === 'activity' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" data-id="element-945" />}
            </button>
            <button onClick={() => setActiveTab('emails')} className={`flex-1 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors relative ${activeTab === 'emails' ? 'text-primary' : 'text-text-tertiary hover:text-text-secondary'}`} data-id="element-946">
              <span className="flex items-center justify-center gap-1.5" data-id="element-947">
                <MailIcon className="w-3.5 h-3.5" data-id="element-948" />
                Emails Sent
                <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-teal-100 text-teal-700 text-[10px] font-bold" data-id="element-949">
                  {emailEntries.length}
                </span>
              </span>
              {activeTab === 'emails' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" data-id="element-950" />}
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto" data-id="element-951">
            {displayedEntries.length === 0 ? <div className="px-4 py-6 text-center text-sm text-text-tertiary" data-id="element-952">
                {activeTab === 'activity' ? 'No notifications yet.' : 'No emails sent yet.'}
              </div> : <div className="divide-y divide-border" data-id="element-953">
                {displayedEntries.map(entry => <div key={entry.id} className={`px-4 py-3 hover:bg-surface transition-colors cursor-pointer ${!entry.read ? 'bg-primary/5' : ''}`} onClick={() => {
            if (!entry.read) onMarkRead(entry.id);
          }} data-id="element-954">
                    <div className="flex gap-3" data-id="element-955">
                      <div className="flex-shrink-0 mt-0.5" data-id="element-956">
                        {getIcon(entry.type)}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-957">
                        <p className={`text-sm ${!entry.read ? 'font-semibold text-text-primary' : 'text-text-secondary'}`} data-id="element-958">
                          {entry.message}
                        </p>
                        {entry.type === 'email' && entry.emailTo && <p className="text-xs text-teal-600 mt-0.5 flex items-center gap-1" data-id="element-959">
                            <span className="font-medium" data-id="element-960">To:</span>{' '}
                            {entry.emailTo}
                          </p>}
                        {entry.type === 'email' && entry.emailSubject && <p className="text-xs text-text-tertiary mt-0.5 italic truncate" data-id="element-961">
                            "{entry.emailSubject}"
                          </p>}
                        {entry.detail && <p className="text-xs text-text-tertiary mt-0.5 truncate" data-id="element-962">
                            {entry.detail}
                          </p>}
                        <p className="text-[10px] text-text-tertiary mt-1" data-id="element-963">
                          {getRelativeTime(entry.timestamp)}
                        </p>
                      </div>
                      {!entry.read && <div className="flex-shrink-0 flex items-center" data-id="element-964">
                          <span className="h-2 w-2 bg-primary rounded-full" data-id="element-965"></span>
                        </div>}
                    </div>
                  </div>)}
              </div>}
          </div>
        </div>}
    </div>;
}